/**
 * impersonation — client-side state for a platform support session.
 *
 * A tenantadmin can sign in AS an institute user to reproduce a reported
 * problem. Because the operator console (lms.lmscampus.com) and the institute
 * LMS (abc.lmscampus.com / a custom domain) are different origins with
 * different localStorage, the handoff is a one-time ticket redeemed by
 * ImpersonateLanding on the institute's origin — see
 * vc_be/services/impersonationService.js for the other half.
 *
 * Two storage keys:
 *   vc-impersonation      — banner data for the active support session
 *   vc-impersonation-prev — the operator's OWN token/user, saved only when the
 *                           two origins happen to be the same (local dev, or an
 *                           institute with no separate host). Restoring it on
 *                           exit is what stops a support login from silently
 *                           logging the operator out of their own console.
 */

const KEY = 'vc-impersonation';
const PREV_KEY = 'vc-impersonation-prev';

/** Active support session details, or null. */
export function getImpersonation() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isImpersonating() {
  return !!getImpersonation();
}

/** True when the session may only issue GET requests. */
export function isReadOnlySupportSession() {
  return !!getImpersonation()?.readOnly;
}

/**
 * Take over this tab as the impersonated user.
 *
 * Saves the current token/user first so a same-origin operator gets their own
 * session back on exit; on a real cross-origin handoff there is nothing to save
 * and the operator's console tab is untouched anyway.
 */
export function beginImpersonation({ token, user, impersonation }) {
  const prevToken = localStorage.getItem('token');
  const prevUser = localStorage.getItem('user');
  if (prevToken && prevUser) {
    try {
      localStorage.setItem(PREV_KEY, JSON.stringify({ token: prevToken, user: prevUser }));
    } catch { /* quota — exit still works, it just can't restore */ }
  } else {
    localStorage.removeItem(PREV_KEY);
  }

  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem(KEY, JSON.stringify(impersonation));
  // Branch selection belongs to whoever was here before — a stale X-Branch-Id
  // would scope the support session to a branch the operator never chose.
  localStorage.removeItem('vc-selected-branch');
  localStorage.removeItem('selectedRole');
  window.dispatchEvent(new Event('vc-auth-change'));
}

/**
 * Clear the support session locally and hand back the operator's own session
 * when there was one on this origin.
 *
 * @returns {{ restored: boolean, returnUrl: string|null }}
 *   restored — the operator's own session is back in localStorage on this origin
 *   returnUrl — the console page to send them to when it isn't
 */
export function clearImpersonation() {
  const imp = getImpersonation();
  localStorage.removeItem(KEY);
  localStorage.removeItem('vc-selected-branch');
  localStorage.removeItem('selectedRole');

  let restored = false;
  try {
    const raw = localStorage.getItem(PREV_KEY);
    if (raw) {
      const prev = JSON.parse(raw);
      if (prev?.token && prev?.user) {
        localStorage.setItem('token', prev.token);
        localStorage.setItem('user', prev.user);
        restored = true;
      }
    }
  } catch { /* fall through to a plain sign-out */ }
  localStorage.removeItem(PREV_KEY);

  if (!restored) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  window.dispatchEvent(new Event('vc-auth-change'));
  return { restored, returnUrl: imp?.returnUrl || null };
}

/** Landing path for the impersonated user's role. */
export function homePathFor(usertype) {
  if (usertype === 'student') return '/s/dashboard';
  if (usertype === 'instructor') return '/i/';
  return '/dashboard';
}
